
import { graft as defaultGraft, IPatch } from '../../utilities/Graft';
import { createActionCreator, IActionCreator, IAction } from '../actions/createActionCreator';
import { AutoKeyFacet } from '../../models/AutoKeyFacet';

/**
 * @internal
 */
export const cacheKeyFacet: AutoKeyFacet = new AutoKeyFacet('cacheKey');

/**
 * @public
 */
export interface IKeyMapState {
  readonly lastAutoId: number;
  readonly mappings: {
    [domainKey: string]: string;
  };
}

const INITIAL_KEY_MAP_STATE: IKeyMapState = {
  lastAutoId: 0,
  mappings: {}
};

/**
 * @public
 */
export interface IAddKeysPayload {
  keys: string[];
}

/**
 * @public
 */
export interface IMapKeysPayload {
  mappings: {
    [fromKey: string]: string;
  };
}

/**
 * @public
 */
export const addKeys: IActionCreator<'addKeys', IAddKeysPayload> = createActionCreator('addKeys');
/**
 * @public
 */
export const mapKeys: IActionCreator<'mapKeys', IMapKeysPayload> = createActionCreator('mapKeys');

/**
 * @internal
 */
export function keyMapReducer(state: IKeyMapState = INITIAL_KEY_MAP_STATE, action?: IAction | undefined, options: {
  graft?: typeof defaultGraft;
} = {}): IKeyMapState {
  const {
    graft = defaultGraft
  } = options;

  if (action) {
    if (addKeys.matches(action)) {
      const {
        keys
      } = action.payload;

      let {
        lastAutoId
      } = state;

      const patch: IPatch<{
        [domainKey: string]: string;
      }> = {};

      for (const key of keys) {
        if (!state.mappings[key]) {
          patch[key] = cacheKeyFacet.serialize({
            id: ++lastAutoId
          });
        }
      }

      return graft(state, {
        lastAutoId: lastAutoId,
        mappings: patch
      });
    } else if (mapKeys.matches(action)) {
      const {
        mappings
      } = action.payload;

      const patch: IKeyMapState['mappings'] = {};

      const newKeys: {
        [domainKey: string]: string;
      } = {};

      let {
        lastAutoId
      } = state;

      for (const fromKey in mappings) {
        if (Object.prototype.hasOwnProperty.call(mappings, fromKey)) {
          const toKey = mappings[fromKey];

          const cacheKey = state.mappings[toKey] || newKeys[toKey];

          if (cacheKey) {
            patch[fromKey] = cacheKey;
          } else {
            const newCacheKey = cacheKeyFacet.serialize({
              id: ++lastAutoId
            });

            patch[fromKey] = newKeys[toKey] = newCacheKey;
          }
        }
      }

      return graft(state, {
        lastAutoId: lastAutoId,
        mappings: {
          ...newKeys,
          ...patch
        }
      });
    }
  }

  return state;
}

/**
 * @internal
 */
export function getMappedKey(state: IKeyMapState, domainKey: string): string {
  return state.mappings[domainKey];
}

/**
 * @internal
 */
export function remapKeys<T>(keyMapState: IKeyMapState, input: { [key: string]: T; }): { [key: string]: T; } {
  const output: typeof input = {};

  const keys = Object.keys(input);

  for (const key of keys) {
    const cacheKey = getMappedKey(keyMapState, key);

    output[cacheKey] = input[key];
  }

  return output;
}
